// src/productions/ProductionDashboard.js
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation, useParams } from 'react-router-dom';
import { API_BASE, fetchJson, fetchForm } from '../api';
import {
  Box,
  Typography,
  CircularProgress,
  Breadcrumbs,
  Link,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Grid,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import LinkIcon from '@mui/icons-material/Link';
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';

/* ==== Destinatari (etichette UI) ==== */
const AUDIENCE_LABEL = {
  visione_interna: 'Visione interna',
  capo_progetto: 'Capo progetto',
  autori: 'Autori',
  produttore: 'Produttore',
  rete: 'Visione rete',
  curatore_editoriale: 'Curatore editoriale',
};

/**
 * Wizard minimale inline per evitare errori di import.
 * Puoi sostituirlo in futuro con il tuo PostPlanWizard reale (stessa firma props).
 */
function PostPlanWizardInline({ productionId, onClose }) {
  const steps = ['Setup', 'Review', 'Timeline'];
  const [activeStep, setActiveStep] = useState(0);

  // campi demo: sostituisci con i reali del tuo wizard
  const [planName, setPlanName] = useState('');
  const [notes, setNotes] = useState('');
  const [timeline, setTimeline] = useState('');

  const handleNext = async () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((s) => s + 1);
      return;
    }
    // Ultimo step -> salva
    try {
      const payload = {
        productionId,
        name: planName || 'Piano senza nome',
        notes: notes || null,
        timeline: timeline || null,
      };
      const resp = await fetch(`${API_BASE}/postplans`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.error || `HTTP ${resp.status}`);
      }
      alert('Piano salvato ✔');
      onClose?.(data);
    } catch (e) {
      alert(`Errore salvataggio piano: ${e?.message || e}`);
    }
  };

  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));

  return (
    <Box sx={{ p: 3, border: '1px solid #e9ecef', borderRadius: 2, background: '#fff' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Wizard Piano di Post Produzione
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Contenuto step semplificato */}
      <Box sx={{ display: 'grid', gap: 2, mb: 3 }}>
        {activeStep === 0 && (
          <TextField
            label="Versione piano"
            value={planName}
            onChange={(e) => setPlanName(e.target.value)}
            fullWidth
          />
        )}
        {activeStep === 1 && (
          <TextField
            label="Note / Obiettivi"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={4}
            fullWidth
          />
        )}
        {activeStep === 2 && (
          <TextField
            label="Timeline (riassunto)"
            value={timeline}
            onChange={(e) => setTimeline(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button disabled={activeStep === 0} onClick={handleBack}>
          Indietro
        </Button>
        <Button variant="contained" onClick={handleNext}>
          {activeStep === steps.length - 1 ? 'Salva' : 'Avanti'}
        </Button>
      </Box>
    </Box>
  );
}


function StatoLavori({ planId }) {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!planId) { setLoading(false); return; }
    fetchJson(`/postplans/${planId}/stato-lavori`)
      .then(d => setBlocks(d.blocks || []))
      .catch(() => setBlocks([]))
      .finally(() => setLoading(false));
  }, [planId]);

  if (!planId) return null;
  if (loading) return <CircularProgress size={16} sx={{ color: '#d4a550', mt: 1 }} />;
  if (blocks.length === 0) return (
    <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 1 }}>Nessuna lavorazione in corso oggi.</Typography>
  );

  // Raggruppa per episodio
  const byEp = {};
  blocks.forEach(b => {
    const k = b.episode_no;
    if (!byEp[k]) byEp[k] = [];
    byEp[k].push(b);
  });

  function scadenzaLabel(gg) {
    const g = Number(gg);
    if (g < 0) return { label: 'scaduto', color: '#f87171' };
    if (g === 0) return { label: 'oggi', color: '#facc15' };
    if (g === 1) return { label: 'domani', color: '#fb923c' };
    return { label: `tra ${g} gg`, color: '#86efac' };
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {Object.keys(byEp).sort((a,b) => Number(a)-Number(b)).map(epNo => (
        <Box key={epNo} sx={{ p: 1.5, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', background: 'rgba(255,255,255,0.02)' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#d4a550', mb: 1 }}>EP. {epNo}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {byEp[epNo].map((b, i) => {
              const { label, color } = scadenzaLabel(b.giorni_mancanti);
              return (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 12, color: '#e8e6df', fontWeight: 600, minWidth: 120 }}>{b.fase}</Typography>
                  {b.part_label && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{b.part_label}</Typography>}
                  {b.assegnatario && <Typography sx={{ fontSize: 11, color: 'rgba(212,165,80,0.7)' }}>· {b.assegnatario}</Typography>}
                  <Box sx={{ ml: 'auto', fontSize: 11, fontWeight: 700, color, bgcolor: `${color}18`, border: `1px solid ${color}40`, borderRadius: 10, px: 1, py: 0.2 }}>
                    {label}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default function ProductionDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const _u = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const _blocked = false; // PostPlan riaperto
  const [prod, setProd] = useState(null);
  const [editProdOpen, setEditProdOpen] = useState(false);
  const [editProdForm, setEditProdForm] = useState({});
  const [editProdSaving, setEditProdSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // stato piani di post
  const [showWizard, setShowWizard] = useState(false);
  const [plans, setPlans] = useState([]);
  const wizardPlan = useMemo(
  () => plans.find(p => (p.name || '').startsWith('[WIZARD]')),
  [plans]
);
  const [plansLoading, setPlansLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importProductions, setImportProductions] = useState([]);
  const [importSelectedProd, setImportSelectedProd] = useState('');
  const [importPlans, setImportPlans] = useState([]);
  const [importSelectedPlan, setImportSelectedPlan] = useState('');
  const [importTxDate, setImportTxDate] = useState('');
  const [importLoading, setImportLoading] = useState(false);

  async function loadImportProductions() {
    if (!prod?.show_id) return;
    try {
      // Carica tutte le produzioni dello stesso show eccetto quella corrente
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`${API_BASE}/productions`, { headers: { Authorization: `Bearer ${token}` } });
      const all = await res.json();
      const filtered = all.filter(p => p.show_id === prod.show_id && String(p.id) !== String(prod.id));
      setImportProductions(filtered);
    } catch(e) {}
  }

  async function loadImportPlans(productionId) {
    try {
      const data = await fetchJson(`/postplans?productionId=${productionId}`);
      setImportPlans(data.plans || []);
    } catch(e) {}
  }

  async function executeImport() {
    if (!importSelectedPlan || !importTxDate) return;
    setImportLoading(true);
    try {
      const data = await fetchJson(`/postplans/${importSelectedPlan}/duplicate-to-production`, {
        method: 'POST',
        body: JSON.stringify({ targetProductionId: prod.id, txStartDate: importTxDate }),
      });
      if (data.ok) {
        setShowImportModal(false);
        navigate(`/piani/new?planId=${data.newPlanId}&productionId=${prod.id}`);
      }
    } catch(e) { alert(e.message); }
    finally { setImportLoading(false); }
  }
  const [rosa, setRosa] = useState([]);
  const [veterans, setVeterans] = useState([]);
  const [squad, setSquad] = useState([]);

  // stati per Revisioni
  const [revEpisode, setRevEpisode] = useState('');
  const [revAudience, setRevAudience] = useState('autori');
  const [revFile, setRevFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [revsLoading, setRevsLoading] = useState(false);
  // stati per Autori
  const [authors, setAuthors] = useState([]);
  const [collaborations, setCollaborations] = useState({}); // key: "authorId_assistantId" -> times
  const [authorsLoading, setAuthorsLoading] = useState(false);
  const [showAuthorsModal, setShowAuthorsModal] = useState(false);
  const [authorForm, setAuthorForm] = useState({ function: 'montaggio', freelancer_id: null, name_manual: '', works_alone: true, assistant_freelancer_id: null, assistant_name_manual: '' });
  const [authorSearch, setAuthorSearch] = useState('');
  const [authorSearchResults, setAuthorSearchResults] = useState([]);
  const [assistantSearch, setAssistantSearch] = useState('');

  // Squadra di post (derivata dai piani)
  const [team, setTeam] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);

  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  // Funzioni autori
  const fetchCollaboration = async (authorId, assistantId) => {
    if (!authorId || !assistantId) return;
    const key = `${authorId}_${assistantId}`;
    if (collaborations[key] !== undefined) return;
    try {
      const res = await fetchJson(`/productions/authors/collaborations?authorId=${authorId}&assistantId=${assistantId}`);
      setCollaborations(prev => ({ ...prev, [key]: res.times || 0 }));
    } catch(e) {}
  };

  const AUTHOR_FUNCTIONS = [
    { value: 'finalizzazione', label: 'Finalizzazione' },
    { value: 'montaggio', label: 'Montaggio' },
    { value: 'taglio', label: 'Taglio contenuti' },
  ];
  const searchAuthors = async (q) => {
    setAuthorSearch(q);
    try {
      const res = await fetchJson(`/authors/search?q=${encodeURIComponent(q)}`);
      setAuthorSearchResults(Array.isArray(res) ? res : []);
    } catch(e) { setAuthorSearchResults([]); }
  };
  const handleAddAuthor = async () => {
    if (!authorForm.function) return;
    if (!authorForm.freelancer_id && !authorForm.name_manual.trim()) {
      alert('Seleziona un autore dal network o inserisci un nome manuale.'); return;
    }
    try {
      const saved = await fetchJson(`/productions/${id}/authors`, {
        method: 'POST',
        body: JSON.stringify(authorForm),
      });
      setAuthors(prev => [...prev, saved]);
      setAuthorForm({ function: 'montaggio', freelancer_id: null, name_manual: '', works_alone: true, assistant_freelancer_id: null, assistant_name_manual: '' });
      setAuthorSearch('');
      setAuthorSearchResults([]);
      setAssistantSearch('');
      setShowAuthorsModal(false);
    } catch(e) { alert('Errore: ' + e.message); }
  };
  const handleDeleteAuthor = async (authorId) => {
    if (!window.confirm('Rimuovere questo autore?')) return;
    try {
      await fetchJson(`/productions/${id}/authors/${authorId}`, { method: 'DELETE' });
      setAuthors(prev => prev.filter(a => a.id !== authorId));
    } catch(e) { alert('Errore: ' + e.message); }
  };
  const handleToggleWorksAlone = async (author) => {
    try {
      const updated = await fetchJson(`/productions/${id}/authors/${author.id}`, {
        method: 'PUT',
        body: JSON.stringify({ works_alone: !author.works_alone }),
      });
      setAuthors(prev => prev.map(a => a.id === updated.id ? { ...a, works_alone: updated.works_alone } : a));
    } catch(e) { alert('Errore: ' + e.message); }
  };
  const closeSnack = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnack((s) => ({ ...s, open: false }));
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text || '');
      showSnack('Copiato!', 'success');
    } catch (e) {
      console.error(e);
      showSnack('Impossibile copiare', 'error');
    }
  };


  // Raggruppa la squadra per ruolo (derivata dai piani). Hook qui in alto per
  // rispettare le Rules of Hooks (niente hook dopo return condizionali).
  const groupedTeam = useMemo(() => {
    const map = new Map();
    for (const m of team || []) {
      const role = m.role_key || m.roleKey || 'ruolo';
      const name = (m.placeholder_name || m.name || '').trim();
      if (!name) continue;
      if (!map.has(role)) map.set(role, []);
      const arr = map.get(role);
      // unicità per ruolo
      if (!arr.find((x) => x.toLowerCase() === name.toLowerCase())) arr.push(name);
    }
    // ordina ruoli alfabeticamente per ora (in futuro: mappa priorità)
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [team]);

  // note modal-ish state (semplice box sotto)
  const [openNotesFor, setOpenNotesFor] = useState(null); // revision object
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState({ timecode: '', text: '' });

  // carica dettaglio produzione
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token') || '';
        const res = await fetch(`${API_BASE}/productions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        setProd(data);
      } catch (e) {
        console.error('Error loading production', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // carica lista piani per la produzione
  const fetchRosa = async () => {
    try { const d = await fetchJson("/postplans/rosa/" + id); setRosa(d.rosa || []); } catch(e) {}
    try { const d = await fetchJson("/productions/" + id + "/veterans"); setVeterans(d.veterans || []); } catch(e) {}
  };
  const fetchSquad = async () => {
    try { const d = await fetchJson("/postplans/squad-by-production/" + id); setSquad(d.squad || []); } catch(e) {}
    // fetch autori
    try {
      setAuthorsLoading(true);
      const a = await fetchJson(`/productions/${id}/authors`);
      setAuthors(Array.isArray(a) ? a : []);
      // Carica collaborazioni per coppie autore+assistente
      (Array.isArray(a) ? a : []).forEach(author => {
        if (author.freelancer_id && author.assistant_freelancer_id) {
          fetchCollaboration(author.freelancer_id, author.assistant_freelancer_id);
        }
      });
    } catch(e) {} finally { setAuthorsLoading(false); }
  };
  const [publishing, setPublishing] = useState(null);
  const handlePublish = async (planId) => {
    if (!window.confirm('Applicare il piano? I freelancer della squadra riceveranno una email.')) return;
    setPublishing(planId);
    try {
      await fetchJson(`/postplans/${planId}/publish`, { method: 'POST' });
      alert('Piano applicato! Email inviate ai freelancer.');
      fetchPlans();
    } catch(e) { alert('Errore: ' + (e.message||e)); }
    finally { setPublishing(null); }
  };
  const fetchPlans = async () => {
    setPlansLoading(true);
    try {
      const res = await fetch(`${API_BASE}/postplans?productionId=${encodeURIComponent(id)}`);
      const data = await res.json().catch(() => ({}));
      const items = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.plans)
        ? data.plans
        : [];
            const sorted = [...items].sort((a, b) => {
        const av = Number(a?.version) || 0;
        const bv = Number(b?.version) || 0;
        if (bv !== av) return bv - av;
        const at = a?.created_at ? new Date(a.created_at).getTime() : 0;
        const bt = b?.created_at ? new Date(b.created_at).getTime() : 0;
        return bt - at;
      });
      setPlans(sorted);
    } catch (e) {
      console.error('Error loading post plans', e);
      setPlans([]);
    } finally {
      setPlansLoading(false);
    }
  };

  // carica persone/ruoli assegnati nei piani (per comporre la squadra)
  const fetchTeam = async () => {
    setTeamLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/postplans/person-requirements?productionId=${encodeURIComponent(id)}`
      );
      const data = await res.json().catch(() => ({}));
      const items = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.data)
        ? data.data
        : [];
      setTeam(items);
    } catch (e) {
      console.error('Error loading team', e);
      setTeam([]);
    } finally {
      setTeamLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchRosa();
    fetchSquad();
    fetchTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // carica lista revisioni per la produzione
  const fetchRevisions = async () => {
    setRevsLoading(true);
    try {
      // ✅ snake_case: il backend ora accetta anche productionId, ma usiamo production_id
      const res = await fetch(`${API_BASE}/revisions?production_id=${encodeURIComponent(id)}`);
      const data = await res.json().catch(() => []);
      setRevisions(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setRevisions([]);
    } finally {
      setRevsLoading(false);
    }
  };

  useEffect(() => {
    fetchRevisions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // helper: password random di 6 caratteri alfanumerici
  const makePwd = () => Math.random().toString(36).slice(-6);

  // crea nuova revisione (upload + create)
  async function uploadInChunks(file, token) {
    const CHUNK_SIZE = 20 * 1024 * 1024; // 20MB
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const initRes = await fetch(`${API_BASE}/revisions/upload/chunk/init`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ filename: file.name, totalChunks }),
    });
    if (!initRes.ok) throw new Error(`Init fallito: HTTP ${initRes.status}`);
    const { uploadId } = await initRes.json();
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const blob = file.slice(start, end);
      const chunkRes = await fetch(`${API_BASE}/revisions/upload/chunk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-Upload-Id': uploadId,
          'X-Chunk-Index': String(i),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: blob,
      });
      if (!chunkRes.ok) throw new Error(`Chunk ${i} fallito: HTTP ${chunkRes.status}`);
      setUploadProgress(Math.round(((i + 1) / totalChunks) * 95));
    }
    const finalRes = await fetch(`${API_BASE}/revisions/upload/chunk/finalize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ uploadId, filename: file.name, totalChunks }),
    });
    if (!finalRes.ok) throw new Error(`Finalizzazione fallita: HTTP ${finalRes.status}`);
    setUploadProgress(100);
    return await finalRes.json();
  }

  const handleCreateRevision = async () => {
    if (!revEpisode || !revAudience || !revFile) {
      alert('Inserisci episodio, destinatario e file.');
      return;
    }
    try {
      // 1) Upload
      setUploading(true);
      setUploadProgress(0);
      const token = localStorage.getItem('jkl_token') || sessionStorage.getItem('jkl_token') || '';
      const upData = await uploadInChunks(revFile, token);

      const fileUrl = upData.url || upData.file_url || upData.path;
      if (!fileUrl) throw new Error('URL del file non ricevuto dall’upload');

      // 2) Crea revisione — passiamo anche una password generata
      const password = makePwd();
      const data = await fetchJson('/revisions', {
        method: 'POST',
        body: JSON.stringify({
          production_id: Number(id),
          episode: Number(revEpisode),
          audience: revAudience,
          file_url: fileUrl,
          password,
        }),
      });

      // reset + refresh
      setRevEpisode('');
      setRevAudience('autori');
      setRevFile(null);
      setUploading(false);
      setUploadProgress(0);
      fetchRevisions();

      const shareLink = `${window.location.origin}/review/${data.link_slug}`;
      alert(`Revisione creata!\n\nLink: ${shareLink}\nPassword: ${data.password || password}`);
    } catch (e) {
      alert(`Errore creazione revisione: ${e?.message || e}`);
    }
  };

  // carica note per una revisione
  const openNotes = async (rev) => {
    try {
      const res = await fetch(`${API_BASE}/revisions/${rev.id}/notes`);
      const data = await res.json().catch(() => []);
      setNotes(Array.isArray(data) ? data : []);
      setOpenNotesFor(rev);
    } catch (e) {
      alert('Errore caricamento note');
    }
  };

  const addNote = async () => {
    if (!openNotesFor) return;
    if (!newNote.text) {
      alert('Scrivi una nota.');
      return;
    }
    try {
      const resp = await fetch(`${API_BASE}/revisions/${openNotesFor.id}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timecode: newNote.timecode || null, note_text: newNote.text }),
      });
      const created = await resp.json();
      if (!resp.ok) throw new Error(created?.error || `HTTP ${resp.status}`);
      setNotes((n) => [...n, created]);
      setNewNote({ timecode: '', text: '' });
    } catch (e) {
      alert(`Errore creazione nota: ${e?.message || e}`);
    }
  };

  const updateNoteAction = async (noteId, actionText) => {
    try {
      const resp = await fetch(`${API_BASE}/revisions/notes/${noteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_taken: actionText }),
      });
      const updated = await resp.json();
      if (!resp.ok) throw new Error(updated?.error || `HTTP ${resp.status}`);
      setNotes((ns) => ns.map((n) => (n.id === noteId ? updated : n)));
    } catch (e) {
      alert(`Errore aggiornamento azione: ${e?.message || e}`);
    }
  };

  if (loading) return <CircularProgress sx={{ m: 4 }} />;
  if (!prod) return <Typography sx={{ m: 4 }}>Produzione non trovata</Typography>;

  const formatDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    // gestisce date-only o timestamp
    if (Number.isNaN(dt.getTime())) return '—';
    return dt.toLocaleDateString('it-IT');
  };

  const statusLabel = (s) => {
    const key = String(s || '').toLowerCase();
    if (key === 'in_preparazione' || key === 'preparazione') return 'In preparazione';
    if (key === 'in_riprese' || key === 'riprese') return 'In riprese';
    if (key === 'in_post' || key === 'post') return 'In post';
    if (key === 'chiusa') return 'Chiusa';
    if (key === 'archiviata' || key === 'archived') return 'Archiviata';
    return s ? String(s) : '—';
  };

  const prettyRole = (roleKey) => {
    const s = String(roleKey || '').replace(/_/g, ' ').trim();
    if (!s) return 'Ruolo';
    return s
      .split(' ')
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
      .join(' ');
  };

  return (
    <Box>
      <Box sx={{ px:3, pt:2, display:'flex', gap:0.75, alignItems:'center' }}>
        <Typography component={RouterLink} to="/" sx={{ fontSize:11, color:'rgba(232,230,223,0.4)', textDecoration:'none', '&:hover':{color:'#d4a550'} }}>Dashboard</Typography>
        <Typography sx={{ fontSize:11, color:'rgba(232,230,223,0.2)' }}>/</Typography>
        <Typography component={RouterLink} to="/productions" sx={{ fontSize:11, color:'rgba(232,230,223,0.4)', textDecoration:'none', '&:hover':{color:'#d4a550'} }}>Produzioni</Typography>
        <Typography sx={{ fontSize:11, color:'rgba(232,230,223,0.2)' }}>/</Typography>
        <Typography sx={{ fontSize:11, color:'rgba(232,230,223,0.7)', fontWeight:600 }}>{prod.name}</Typography>
      </Box>
      <Box sx={{ mx:3, mt:1.5, mb:3, p:2.5, borderRadius:'12px', border:'1px solid rgba(255,255,255,0.07)', background:'rgba(255,255,255,0.02)', borderLeft:'3px solid rgba(74,222,128,0.5)' }}>
        <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
          <Box>
            <Box sx={{ display:'flex', alignItems:'center', gap:1.5, mb:0.5 }}>
              <Typography sx={{ fontSize:24, fontWeight:500, color:'#e8e6df', fontFamily:"'Kanit','Inter',sans-serif", lineHeight:1.1 }}>
              {prod.show_name || prod.name}
              {prod.season_number && <span style={{ fontSize:14, fontWeight:400, color:'rgba(212,165,80,0.7)', marginLeft:10 }}>Stagione {prod.season_number}</span>}
            </Typography>
              <Box sx={{ px:1, py:0.25, borderRadius:'6px', border:'1px solid rgba(74,222,128,0.4)', background:'rgba(74,222,128,0.08)' }}>
                <Typography sx={{ fontSize:10, fontWeight:700, color:'#4ade80', letterSpacing:'0.07em', textTransform:'uppercase' }}>{statusLabel(prod.status)}</Typography>
              </Box>
            </Box>
            <Typography sx={{ fontSize:13, color:'rgba(232,230,223,0.55)' }}>{prod.client || '—'}{prod.code && <Box component="span" sx={{ ml:1, px:0.75, py:0.15, borderRadius:'4px', background:'rgba(212,165,80,0.12)', border:'1px solid rgba(212,165,80,0.3)', fontSize:11, color:'#d4a550', fontWeight:600 }}>{prod.code}</Box>}</Typography>
            <Typography sx={{ fontSize:11, color:'rgba(232,230,223,0.3)', mt:0.25 }}>Creata il {formatDate(prod.created_at)}</Typography>
          </Box>
          <Button size="small" variant="outlined" onClick={() => { setEditProdForm({ name: prod.name||'', client: prod.client||'', code: prod.code||'', shooting_start_date: prod.shooting_start_date?prod.shooting_start_date.slice(0,10):'', post_start_date: prod.post_start_date?prod.post_start_date.slice(0,10):'', air_date: prod.air_date?prod.air_date.slice(0,10):'' }); setEditProdOpen(true); }} sx={{ borderColor:'rgba(212,165,80,0.4)', color:'#d4a550', fontSize:12, flexShrink:0 }}>Modifica</Button>
        </Box>
        <Box sx={{ display:'flex', gap:3, mt:2, pt:2, borderTop:'1px solid rgba(255,255,255,0.06)', flexWrap:'wrap' }}>
          {[['RIPRESE', prod.shooting_start_date], ['INIZIO POST', prod.post_start_date], ['MESSA IN ONDA', prod.air_date]].map(([label, date]) => (
            <Box key={label}>
              <Typography sx={{ fontSize:9, fontWeight:700, color:'rgba(232,230,223,0.3)', letterSpacing:'0.08em', textTransform:'uppercase', mb:0.25 }}>{label}</Typography>
              <Typography sx={{ fontSize:14, fontWeight:700, color:'#e8e6df' }}>{formatDate(date)}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Sezioni HOT in due colonne (md+) */}
      <Grid container spacing={3} sx={{ mb:4, px:3 }}>
        {/* Colonna sinistra: Piani di Post */}
        <Grid item xs={12} md={6}>
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:1.5 }}>
            <Typography sx={{ fontSize:13, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', color:'text.secondary' }}>Piani di Post-Produzione</Typography>
            <Box sx={{ display:'flex', gap:1 }}>
            {!_blocked && <Button size="small" onClick={() => navigate(`/postplans/new?productionId=${encodeURIComponent(prod.id)}`)} sx={{ fontSize:12, fontWeight:600, color:'#d4a550', border:'1px solid rgba(212,165,80,0.4)', borderRadius:'6px', px:1.5, py:0.5, '&:hover':{ background:'rgba(212,165,80,0.08)' } }}>+ Nuovo Piano</Button>}
            {!_blocked && prod?.show_id && <Button size="small" onClick={() => { setShowImportModal(true); loadImportProductions(); }} sx={{ fontSize:12, fontWeight:600, color:'rgba(212,165,80,0.7)', border:'1px solid rgba(212,165,80,0.25)', borderRadius:'6px', px:1.5, py:0.5, '&:hover':{ background:'rgba(212,165,80,0.06)' } }}>⬇ Importa piano</Button>}
          </Box>
          </Box>
          {plansLoading ? <CircularProgress size={20} /> : plans.length === 0 ? (
            <Box sx={{ p:2, border:'1px dashed rgba(255,255,255,0.08)', borderRadius:'8px', textAlign:'center' }}>
              <Typography sx={{ fontSize:12, color:'text.disabled' }}>Nessun piano disponibile.</Typography>
            </Box>
          ) : (
            <Box sx={{ display:'flex', flexDirection:'column', gap:1 }}>
              {plans.map((p) => {
                const delivery = p.delivery_deadline ? formatDate(p.delivery_deadline) : '—';
                return (
                  <Box key={p.id} sx={{ p:1.5, border:'1px solid rgba(255,255,255,0.07)', borderRadius:'10px', background:'rgba(255,255,255,0.02)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:1 }}>
                    <Box sx={{ minWidth:0, flex:1 }}>
                      <Typography sx={{ fontSize:14, fontWeight:700, color:'#e8e6df', mb:0.25 }}>{p.name || 'Piano'}</Typography>
                      <Typography sx={{ fontSize:11, color:'text.disabled' }}>Creato: {p.created_at ? formatDate(p.created_at) : '—'}</Typography>
                    </Box>
                    <Box sx={{ display:'flex', gap:0.75, flexShrink:0, alignItems:'center' }}>
                      {_blocked ? (
                        <Button size="small" disabled sx={{ fontSize:12, fontWeight:600, color:'rgba(212,165,80,0.5)', border:'1px solid rgba(212,165,80,0.2)', borderRadius:'6px', px:1.5, py:0.5, minWidth:0 }}>🚧 In manutenzione</Button>
                      ) : (
                        <>
                          {!p.published_at ? (
                <Button size="small"
                  onClick={() => handlePublish(p.id)}
                  disabled={publishing === p.id}
                  sx={{ fontSize:12, fontWeight:600, color:'#4ade80', border:'1px solid rgba(74,222,128,0.4)', borderRadius:'6px', px:1.5, py:0.5, minWidth:0, '&:hover':{ background:'rgba(74,222,128,0.08)' } }}>
                  {publishing === p.id ? '...' : '▶ Applica'}
                </Button>
              ) : (
                <Button size="small" disabled
                  sx={{ fontSize:11, color:'rgba(74,222,128,0.6)', border:'1px solid rgba(74,222,128,0.2)', borderRadius:'6px', px:1.5, py:0.5, minWidth:0 }}>
                  ✓ Applicato
                </Button>
              )}
              <Button size="small" onClick={() => navigate(`/piani/${p.id}/timeline`)}
                            sx={{ fontSize:12, fontWeight:600, color:'#d4a550', border:'1px solid rgba(212,165,80,0.4)', borderRadius:'6px', px:1.5, py:0.5, minWidth:0, background:'rgba(212,165,80,0.06)', '&:hover':{ background:'rgba(212,165,80,0.14)' } }}>Apri</Button>
                          <Button size="small" onClick={() => navigate(`/postplans/new?productionId=${encodeURIComponent(prod.id)}&planId=${p.id}`)}
                            sx={{ fontSize:12, color:'rgba(232,230,223,0.5)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', px:1.5, py:0.5, minWidth:0, '&:hover':{ borderColor:'rgba(212,165,80,0.4)', color:'#d4a550' } }}>Modifica</Button>
                          <Button size="small" onClick={async () => { try { await fetchJson(`/postplans/${p.id}/duplicate`, { method:'POST' }); fetchPlans(); } catch(e) { alert(e.message); } }}
                            sx={{ fontSize:12, color:'rgba(232,230,223,0.5)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', px:1.5, py:0.5, minWidth:0, '&:hover':{ borderColor:'rgba(255,255,255,0.2)', color:'text.primary' } }}>Duplica</Button>
                        </>
                      )}
                      <IconButton size="small" onClick={async () => { if (!window.confirm('Eliminare questo piano?')) return; try { await fetchJson(`/postplans/${p.id}`, { method:'DELETE' }); fetchPlans(); } catch(e) { alert(e.message); } }}
                        sx={{ color:'rgba(239,68,68,0.35)', p:0.5, '&:hover':{ color:'#f87171', background:'rgba(239,68,68,0.08)' } }}>
                        <span style={{ fontSize:15, lineHeight:1 }}>×</span>
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

        {/* Stato Lavori */}
        {plans.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontSize:13, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', color:'text.secondary', mb:1.5 }}>
              Stato Lavori — Oggi
            </Typography>
            <StatoLavori planId={plans[0]?.id} />
          </Box>
        )}

        {/* Squadra confermata */}
        <Box sx={{ mt:3 }}>
          <Typography sx={{ fontSize:13,fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',color:'text.secondary',mb:1.5 }}>Squadra</Typography>
          <Paper sx={{ p:2,border:'1px solid rgba(255,255,255,0.07)',background:'rgba(255,255,255,0.02)',borderRadius:'12px' }}>
          {(() => {
            const GERARCHIA = ['Montaggio','Finalizzatore','Assemblaggio','Modifiche post-Autore','Modifiche post-Rete','Color','Ingest','Logger','Taglio contenuti','Audio','Consegna Master'];
            const byRole = {};
            squad.forEach(r => { const ruolo=r.phase_name||'Altro'; if(!byRole[ruolo]) byRole[ruolo]=[]; const nome=r.nome?(r.nome+' '+(r.cognome||'')).trim():r.name_override||r.label; if(nome) byRole[ruolo].push({nome,isJkl:!!r.freelancer_id}); });
            // Raggruppa anche sort_order per fase
            const phaseSortOrder = {};
            squad.forEach(r => { if (r.phase_name && r.sort_order != null) phaseSortOrder[r.phase_name] = r.sort_order; });
            const ruoliOrdinati = Object.keys(byRole).sort((a,b) => {
              const sa = phaseSortOrder[a] ?? 999;
              const sb = phaseSortOrder[b] ?? 999;
              return sa - sb;
            });
            if (ruoliOrdinati.length === 0) return <Typography sx={{fontSize:12,color:'text.disabled'}}>Nessuna squadra assegnata nel piano.</Typography>;
            return (
              <Box sx={{display:'flex',flexDirection:'column',gap:0.75}}>
                {ruoliOrdinati.map(ruolo => (
                  <Box key={ruolo} sx={{display:'flex',alignItems:'flex-start',gap:1.5,py:0.75,borderBottom:'1px solid rgba(255,255,255,0.05)'}}>
                    <Typography sx={{fontSize:11,fontWeight:700,color:'rgba(212,165,80,0.7)',minWidth:130,textTransform:'uppercase',letterSpacing:'0.04em'}}>{ruolo.split('/')[0].trim()}</Typography>
                    <Box sx={{display:'flex',flexWrap:'wrap',gap:0.5}}>
                      {byRole[ruolo].map((p,i) => (
                        <Box key={i} sx={{display:'flex',alignItems:'center',gap:0.5,background:'rgba(255,255,255,0.04)',borderRadius:'6px',px:1,py:0.25}}>
                          <Typography sx={{fontSize:12,color:'text.primary'}}>{p.nome}</Typography>
                          {p.isJkl && <Typography sx={{fontSize:9,color:'#d4a550',border:'1px solid rgba(212,165,80,0.35)',borderRadius:'3px',px:0.5}}>◈</Typography>}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            );
          })()}
          </Paper>
        </Box>

        {/* Box Autori */}
        <Box sx={{ mt:3 }}>
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:1.5 }}>
            <Typography sx={{ fontSize:13,fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',color:'text.secondary' }}>Autori</Typography>
            <Box
              onClick={() => { setShowAuthorsModal(true); searchAuthors(''); }}
              sx={{ fontSize:11,color:'rgba(212,165,80,0.7)',cursor:'pointer',border:'1px solid rgba(212,165,80,0.25)',borderRadius:'6px',px:1.5,py:0.5,'&:hover':{background:'rgba(212,165,80,0.08)'} }}
            >+ Aggiungi</Box>
          </Box>
          <Paper sx={{ p:2,border:'1px solid rgba(255,255,255,0.07)',background:'rgba(255,255,255,0.02)',borderRadius:'12px' }}>
            {authorsLoading ? (
              <Typography sx={{fontSize:12,color:'text.disabled'}}>Caricamento...</Typography>
            ) : authors.length === 0 ? (
              <Typography sx={{fontSize:12,color:'text.disabled'}}>Nessun autore aggiunto.</Typography>
            ) : (
              <Box sx={{display:'flex',flexDirection:'column',gap:0.75}}>
                {AUTHOR_FUNCTIONS.map(fn => {
                  const group = authors.filter(a => a.function === fn.value);
                  if (group.length === 0) return null;
                  return (
                    <Box key={fn.value}>
                      {fn.value !== 'taglio' ? (
                        <Box sx={{display:'flex',alignItems:'flex-start',gap:1.5,py:0.75,borderBottom:'1px solid rgba(255,255,255,0.05)'}}>
                          <Typography sx={{fontSize:11,fontWeight:700,color:'rgba(212,165,80,0.7)',minWidth:130,textTransform:'uppercase',letterSpacing:'0.04em'}}>{fn.label}</Typography>
                          <Box sx={{display:'flex',flexWrap:'wrap',gap:0.5}}>
                            {group.map(a => (
                              <Box key={a.id} sx={{display:'flex',alignItems:'center',gap:0.5,background:'rgba(255,255,255,0.04)',borderRadius:'6px',px:1,py:0.25}}>
                                <Typography sx={{fontSize:12,color:'text.primary'}}>{a.freelancer_name || a.name_manual}</Typography>
                                {a.freelancer_id && <Typography sx={{fontSize:9,color:'#d4a550',border:'1px solid rgba(212,165,80,0.35)',borderRadius:'3px',px:0.5}}>◈</Typography>}
                                <Box onClick={() => handleDeleteAuthor(a.id)} sx={{fontSize:11,color:'rgba(248,113,113,0.5)',cursor:'pointer',ml:0.5,'&:hover':{color:'#f87171'}}}>×</Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{display:'flex',flexDirection:'column',gap:0.5,py:0.75,borderBottom:'1px solid rgba(255,255,255,0.05)'}}>
                          <Typography sx={{fontSize:11,fontWeight:700,color:'rgba(212,165,80,0.7)',textTransform:'uppercase',letterSpacing:'0.04em',mb:0.5}}>{fn.label}</Typography>
                          {group.map(a => (
                            <Box key={a.id} sx={{display:'flex',alignItems:'center',gap:1.5,background:'rgba(255,255,255,0.03)',borderRadius:'8px',px:1.5,py:0.75}}>
                              <Box sx={{flex:1}}>
                                <Box sx={{display:'flex',alignItems:'center',gap:0.5}}>
                                  <Typography sx={{fontSize:12,color:'text.primary',fontWeight:600}}>{a.freelancer_name || a.name_manual}</Typography>
                                  {a.freelancer_id && <Typography sx={{fontSize:9,color:'#d4a550',border:'1px solid rgba(212,165,80,0.35)',borderRadius:'3px',px:0.5}}>◈</Typography>}
                                </Box>
                                {!a.works_alone && (
                                  <Typography sx={{fontSize:11,color:'text.secondary',mt:0.25}}>
                                    con {a.assistant_name || a.assistant_name_manual || '—'}
                                    {a.freelancer_id && a.assistant_freelancer_id && collaborations[`${a.freelancer_id}_${a.assistant_freelancer_id}`] > 0 && (
                                      <Typography component="span" sx={{fontSize:9,color:'rgba(212,165,80,0.7)',border:'1px solid rgba(212,165,80,0.25)',borderRadius:'4px',px:0.5,ml:0.5}}>
                                        già {collaborations[`${a.freelancer_id}_${a.assistant_freelancer_id}`]}×
                                      </Typography>
                                    )}
                                    {(a.assistant_freelancer_id) && <Typography component="span" sx={{fontSize:9,color:'#d4a550',border:'1px solid rgba(212,165,80,0.35)',borderRadius:'3px',px:0.5,ml:0.5}}>◈</Typography>}
                                  </Typography>
                                )}
                              </Box>
                              <Box
                                onClick={() => handleToggleWorksAlone(a)}
                                sx={{fontSize:10,cursor:'pointer',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'6px',px:1,py:0.25,color: a.works_alone ? 'text.disabled' : 'rgba(212,165,80,0.8)','&:hover':{borderColor:'rgba(212,165,80,0.3)'}}}
                              >{a.works_alone ? 'Solo' : 'Con assistente'}</Box>
                              <Box onClick={() => handleDeleteAuthor(a.id)} sx={{fontSize:11,color:'rgba(248,113,113,0.5)',cursor:'pointer','&:hover':{color:'#f87171'}}}>×</Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Box>

        {/* Modal aggiungi autore */}
        {showAuthorsModal && (
          <Box sx={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={() => setShowAuthorsModal(false)}>
            <Box sx={{background:'#111118',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'16px',p:3,minWidth:400,maxWidth:500}} onClick={e => e.stopPropagation()}>
              <Typography sx={{fontSize:14,fontWeight:700,mb:2,color:'text.primary'}}>Aggiungi autore</Typography>

              <Typography sx={{fontSize:11,color:'text.secondary',mb:0.5}}>Funzione</Typography>
              <Box sx={{display:'flex',gap:1,mb:2}}>
                {AUTHOR_FUNCTIONS.map(fn => (
                  <Box key={fn.value}
                    onClick={() => setAuthorForm(f => ({...f, function: fn.value}))}
                    sx={{fontSize:12,px:1.5,py:0.5,borderRadius:'6px',cursor:'pointer',border:'1px solid',borderColor: authorForm.function === fn.value ? 'rgba(212,165,80,0.5)' : 'rgba(255,255,255,0.1)',background: authorForm.function === fn.value ? 'rgba(212,165,80,0.1)' : 'transparent',color: authorForm.function === fn.value ? '#d4a550' : 'text.secondary'}}
                  >{fn.label}</Box>
                ))}
              </Box>

              <Typography sx={{fontSize:11,color:'text.secondary',mb:0.5}}>Cerca nel network JKL</Typography>
              <input
                value={authorSearch}
                onChange={e => searchAuthors(e.target.value)}
                placeholder="Nome autore..."
                style={{width:'100%',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'8px',padding:'8px 12px',color:'#f0ece4',fontSize:13,outline:'none',marginBottom:4,boxSizing:'border-box'}}
              />
              {authorSearchResults.length > 0 && (
                <Box sx={{background:'#18181f',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'8px',mb:1.5,maxHeight:160,overflowY:'auto'}}>
                  {authorSearchResults.map(r => (
                    <Box key={r.freelancer_id}
                      onClick={() => { setAuthorForm(f => ({...f, freelancer_id: r.freelancer_id, name_manual: ''})); setAuthorSearch(r.name); setAuthorSearchResults([]); }}
                      sx={{px:1.5,py:1,cursor:'pointer',fontSize:12,color:'text.primary','&:hover':{background:'rgba(255,255,255,0.05)'},display:'flex',alignItems:'center',gap:1}}
                    >
                      <Typography sx={{fontSize:12}}>{r.name}</Typography>
                      <Typography sx={{fontSize:9,color:'#d4a550',border:'1px solid rgba(212,165,80,0.35)',borderRadius:'3px',px:0.5}}>◈</Typography>
                    </Box>
                  ))}
                </Box>
              )}

              <Typography sx={{fontSize:11,color:'text.secondary',mb:0.5,mt:1}}>Oppure inserimento manuale</Typography>
              <input
                value={authorForm.name_manual}
                onChange={e => setAuthorForm(f => ({...f, name_manual: e.target.value, freelancer_id: null}))}
                placeholder="Nome e cognome..."
                style={{width:'100%',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'8px',padding:'8px 12px',color:'#f0ece4',fontSize:13,outline:'none',boxSizing:'border-box'}}
              />

              {authorForm.function === 'taglio' && (
                <Box sx={{mt:2}}>
                  <Box sx={{display:'flex',alignItems:'center',gap:1,mb:1}}>
                    <Typography sx={{fontSize:11,color:'text.secondary'}}>Taglia da solo?</Typography>
                    <Box
                      onClick={() => setAuthorForm(f => ({...f, works_alone: !f.works_alone, assistant_freelancer_id: null, assistant_name_manual: ''}))}
                      sx={{fontSize:11,px:1.5,py:0.25,borderRadius:'6px',cursor:'pointer',border:'1px solid',borderColor: authorForm.works_alone ? 'rgba(212,165,80,0.5)' : 'rgba(255,255,255,0.1)',background: authorForm.works_alone ? 'rgba(212,165,80,0.1)' : 'transparent',color: authorForm.works_alone ? '#d4a550' : 'text.secondary'}}
                    >{authorForm.works_alone ? 'Sì' : 'No'}</Box>
                  </Box>
                  {!authorForm.works_alone && (
                    <>
                      <Typography sx={{fontSize:11,color:'text.secondary',mb:0.5}}>Assistente (dalla squadra)</Typography>
                      <select
                        value={authorForm.assistant_freelancer_id || ''}
                        onChange={e => {
                          const sel = squad.filter(s => s.phase_name === 'Taglio contenuti').find(s => String(s.freelancer_id || s.id) === e.target.value);
                          const nome = sel ? (sel.nome ? (sel.nome + ' ' + (sel.cognome||'')).trim() : sel.name_override || sel.label) : '';
                          setAuthorForm(f => ({...f, assistant_freelancer_id: sel?.freelancer_id ? Number(sel.freelancer_id) : null, assistant_name_manual: nome}));
                        }}
                        style={{width:'100%',background:'#18181f',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'8px',padding:'8px 12px',color:'#f0ece4',fontSize:13,outline:'none',boxSizing:'border-box'}}
                      >
                        <option value="">— Seleziona assistente —</option>
                        {squad.filter(s => s.phase_name === 'Taglio contenuti').map((s,i) => {
                          const nome = s.nome ? (s.nome + ' ' + (s.cognome||'')).trim() : s.name_override || s.label;
                          return <option key={s.id || i} value={s.freelancer_id || s.id}>{nome}</option>;
                        })}
                      </select>
                    </>
                  )}
                </Box>
              )}

              <Box sx={{display:'flex',gap:1,mt:2.5,justifyContent:'flex-end'}}>
                <Box onClick={() => setShowAuthorsModal(false)} sx={{fontSize:12,px:2,py:0.75,borderRadius:'8px',cursor:'pointer',border:'1px solid rgba(255,255,255,0.1)',color:'text.secondary','&:hover':{background:'rgba(255,255,255,0.04)'}}}>Annulla</Box>
                <Box onClick={handleAddAuthor} sx={{fontSize:12,px:2,py:0.75,borderRadius:'8px',cursor:'pointer',background:'rgba(212,165,80,0.15)',border:'1px solid rgba(212,165,80,0.35)',color:'#d4a550','&:hover':{background:'rgba(212,165,80,0.25)'}}}>Aggiungi</Box>
              </Box>
            </Box>
          </Box>
        )}

        </Grid>

        {/* Colonna destra: Puntate in visione */}
        <Grid item xs={12} md={6}>
          <Typography sx={{ fontSize:13, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', color:'text.secondary', mb:1.5 }}>
            Puntate in visione
          </Typography>

          {/* FORM NUOVA REVISIONE */}
          <Paper sx={{ p:2, mb:2, border:'1px solid rgba(255,255,255,0.07)', background:'rgba(255,255,255,0.02)', borderRadius:'12px', borderLeft:'3px solid rgba(212,165,80,0.35)' }}>
            <Typography sx={{ fontSize:10, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:'text.disabled', mb:1.5 }}>Nuovo invio</Typography>
            <Box sx={{ display:'grid', gridTemplateColumns:'90px 1fr', gap:1.5, mb:1.5 }}>
              <TextField label="Episodio" type="number" value={revEpisode} onChange={(e) => setRevEpisode(e.target.value)} size="small" />
              <TextField label="Destinatario" select SelectProps={{ native: true }} value={revAudience} onChange={(e) => setRevAudience(e.target.value)} size="small">
                {Object.entries(AUDIENCE_LABEL).map(([val, label]) => (<option key={val} value={val}>{label}</option>))}
              </TextField>
            </Box>
            <Box sx={{ display:'flex', gap:1.5, alignItems:'center', mb:1.5 }}>
              <Button variant="outlined" component="label" size="small" sx={{ borderColor:'rgba(255,255,255,0.15)', color:'text.secondary', fontSize:12 }}>
                Scegli file…
                <input hidden type="file" onChange={(e) => setRevFile(e.target.files?.[0] || null)} />
              </Button>
              <Typography sx={{ fontSize:12, color:'text.disabled' }}>{revFile ? revFile.name : 'Nessun file selezionato'}</Typography>
            </Box>
            <Typography sx={{ fontSize:11, color:'rgba(212,165,80,0.6)', mb:1.5, fontStyle:'italic' }}>
              ⏱ Il file video sarà disponibile per 7 giorni. Le note di revisione restano sempre accessibili.
            </Typography>
            {uploading && (
              <Box sx={{ mb:1.5 }}>
                <Box sx={{ display:'flex', justifyContent:'space-between', mb:0.5 }}>
                  <Typography sx={{ fontSize:11, color:'text.secondary' }}>Caricamento in corso…</Typography>
                  <Typography sx={{ fontSize:11, color:'#d4a550', fontWeight:700 }}>{uploadProgress}%</Typography>
                </Box>
                <Box sx={{ height:4, background:'rgba(255,255,255,0.08)', borderRadius:2, overflow:'hidden' }}>
                  <Box sx={{ height:'100%', width:`${uploadProgress}%`, background:'#d4a550', borderRadius:2, transition:'width 0.3s' }} />
                </Box>
              </Box>
            )}
            <Button variant="contained" fullWidth onClick={handleCreateRevision} disabled={uploading} sx={{ background:'rgba(212,165,80,0.15)', border:'1px solid rgba(212,165,80,0.4)', color:'#d4a550', fontWeight:700, '&:hover':{ background:'rgba(212,165,80,0.25)' } }}>
              Crea link di visione
            </Button>
          </Paper>

          {/* LISTA REVISIONI */}
          <Paper sx={{ p:2, border:'1px solid rgba(255,255,255,0.07)', background:'rgba(255,255,255,0.02)', borderRadius:'12px' }}>
            <Typography sx={{ fontSize:13, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', color:'text.secondary', mb:1.5 }}>Storico invii</Typography>
            {revsLoading ? (
              <CircularProgress size={24} />
            ) : revisions.length === 0 ? (
              <Typography sx={{ fontSize:12, color:'text.disabled' }}>Nessuna revisione inviata.</Typography>
            ) : (
              <Box sx={{ maxHeight:360, overflowY:'auto', overflowX:'hidden', scrollbarWidth:'thin', scrollbarColor:'rgba(212,165,80,0.3) transparent', '&::-webkit-scrollbar':{ width:4 }, '&::-webkit-scrollbar-track':{ background:'transparent' }, '&::-webkit-scrollbar-thumb':{ background:'rgba(212,165,80,0.3)', borderRadius:2 }, pr:1 }}>
                {revisions.map((r) => {
                  const shareLink = `${window.location.origin}/review/${r.link_slug}`;
                  const sentAt = r.created_at || r.sent_at || r.createdAt;
                  const notesCount = r.notes_count ?? 0;
                  return (
                    <Box key={r.id} sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', py:1, borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                      <Box>
                        <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:0.25 }}>
                          <Box sx={{ px:0.75, py:0.1, borderRadius:'4px', background:'rgba(212,165,80,0.15)', border:'1px solid rgba(212,165,80,0.3)' }}>
                            <Typography sx={{ fontSize:10, fontWeight:700, color:'#d4a550' }}>EP. {r.episode}</Typography>
                          </Box>
                          <Typography sx={{ fontSize:13, fontWeight:600, color:'text.primary' }}>{AUDIENCE_LABEL[r.audience] || r.audience}</Typography>
                        </Box>
                        <Typography sx={{ fontSize:11, color:'text.disabled' }}>{sentAt ? formatDate(sentAt) : ''}</Typography>
                      </Box>
                      <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                        <Tooltip title="Copia link" arrow><IconButton size="small" onClick={() => handleCopy(shareLink)}><LinkIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Copia password" arrow><IconButton size="small" onClick={() => handleCopy(r.password || '')}><LockIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Invia via email" arrow><IconButton size="small" onClick={() => { const link=`${window.location.origin}/review/${r.link_slug}`; window.location.href=`mailto:?subject=Link di visione&body=Link: ${encodeURIComponent(link)}%0APassword: ${r.password||''}`; }}><EmailIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Elimina revisione" arrow><IconButton size="small" sx={{ color:'rgba(239,68,68,0.5)', '&:hover':{ color:'#f87171' } }} onClick={async () => { if (!window.confirm('Eliminare questa revisione e tutte le sue note?')) return; try { await fetchJson(`/revisions/${r.id}`, { method:'DELETE' }); fetchRevisions(); } catch(e) { alert(e.message); } }}><span style={{fontSize:16,lineHeight:1}}>×</span></IconButton></Tooltip>
                        <Box sx={{ px:1, py:0.25, borderRadius:'6px', background: notesCount>0 ? 'rgba(212,165,80,0.15)' : 'rgba(255,255,255,0.05)', border: `1px solid ${notesCount>0 ? 'rgba(212,165,80,0.3)' : 'rgba(255,255,255,0.1)'}`, cursor:'pointer' }} onClick={() => navigate(`/productions/${prod.id}/review/${r.id}/notes`)}>
                          <Typography sx={{ fontSize:11, fontWeight:600, color: notesCount>0 ? '#d4a550' : 'text.disabled' }}>{notesCount} note</Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>

        </Grid>
      </Grid>
          {/* PANNELLO NOTE (semplice) */}
          {openNotesFor && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Note — Ep. {openNotesFor.episode} • {AUDIENCE_LABEL[openNotesFor.audience] || openNotesFor.audience}
              </Typography>

              {/* Aggiungi nota */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Timecode (opz.)"
                  value={newNote.timecode}
                  onChange={(e) => setNewNote((s) => ({ ...s, timecode: e.target.value }))}
                  sx={{ minWidth: 160 }}
                />
                <TextField
                  label="Nota"
                  value={newNote.text}
                  onChange={(e) => setNewNote((s) => ({ ...s, text: e.target.value }))}
                  sx={{ flex: 1, minWidth: 320 }}
                />
                <Button variant="contained" onClick={addNote}>Aggiungi</Button>
                <Button onClick={() => { setOpenNotesFor(null); setNotes([]); }}>Chiudi</Button>
              </Box>

              {/* Elenco note */}
              {notes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nessuna nota presente.</Typography>
              ) : (
                <List dense>
                  {notes.map((n) => (
                    <ListItem
                      key={n.id}
                      secondaryAction={
                        <TextField
                          placeholder="Cosa è stato fatto…"
                          value={n.action_taken || ''}
                          onChange={(e) => updateNoteAction(n.id, e.target.value)}
                          size="small"
                          sx={{ minWidth: 220 }}
                        />
                      }
                    >
                      <ListItemText
                        primary={`${n.timecode ? `[${n.timecode}] ` : ''}${n.note_text}`}
                        secondary={n.author ? `Autore: ${n.author}` : null}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          )}

      {/* Rosa */}
      <Box sx={{ mt: 3, mb: 2, px: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}>Rosa opzionati</Typography>
          {prod?.show_id && (
            <Button size="small" variant="outlined" sx={{ fontSize: 11, borderColor: 'rgba(212,165,80,0.4)', color: '#d4a550', '&:hover': { borderColor: '#d4a550', background: 'rgba(212,165,80,0.08)' } }}
              onClick={async () => {
                try {
                  const d = await fetchJson(`/productions/${id}/import-veterans`, { method: 'POST' });
                  if (d.imported > 0) { fetchRosa(); alert(`✓ ${d.imported} veterani importati nella rosa`); }
                  else alert(d.message || 'Nessun veterano trovato');
                } catch(e) { alert(e.message); }
              }}>
              ⭐ Importa veterani
            </Button>
          )}
        </Box>
        <Paper sx={{ p: 2, border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)', borderRadius: '12px' }}>
          {rosa.length === 0 ? (
            <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>Nessuna persona opzionata. <span style={{color:'rgba(212,165,80,0.6)',cursor:'pointer'}} onClick={() => navigate('/squadre')}>→ Vai a Squadre</span></Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {rosa.map((r, i) => {
                const nome = r.nome ? (r.nome + ' ' + (r.cognome||'')).trim() : r.name_override;
                return (
                  <Box key={i} sx={{ display:'flex',alignItems:'center',gap:0.75,background:'rgba(255,255,255,0.04)',borderRadius:'8px',px:1.5,py:0.5,border:'1px solid rgba(255,255,255,0.07)' }}>
                    <Typography sx={{ fontSize:12,color:'text.primary' }}>{nome}</Typography>
                    <Typography sx={{ fontSize:10,color:'rgba(212,165,80,0.7)' }}>{(r.ruoli||[])[0]||r.ruolo||''}</Typography>
                    {r.freelancer_id && <Typography sx={{ fontSize:9,color:'#d4a550',border:'1px solid rgba(212,165,80,0.35)',borderRadius:'3px',px:0.5 }}>◈</Typography>}
                    {veterans.some(v => String(v.freelancer_id) === String(r.freelancer_id)) && <Typography sx={{ fontSize:9,color:'#f0c878',border:'1px solid rgba(240,200,120,0.35)',borderRadius:'3px',px:0.5 }}>⭐ vet</Typography>}
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>
      </Box>
      {/* Modal importa piano */}
      {showImportModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center' }}
          onClick={() => setShowImportModal(false)}>
          <div style={{ background:'#0f0f17',border:'1px solid rgba(212,165,80,0.28)',borderRadius:12,width:480,padding:28,display:'flex',flexDirection:'column',gap:16 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:15,fontWeight:700,color:'#e8e6df',fontFamily:"'Kanit',sans-serif" }}>⬇ Importa piano da stagione precedente</div>
            <div style={{ fontSize:12,color:'rgba(232,230,223,0.45)' }}>Verranno importate fasi, durate e parallelismi. La squadra e i blocchi verranno ricalcolati.</div>

            {/* Selezione produzione */}
            <div>
              <div style={{ fontSize:11,color:'rgba(232,230,223,0.45)',marginBottom:6,textTransform:'uppercase',letterSpacing:'0.08em' }}>Stagione da importare</div>
              <select value={importSelectedProd} onChange={e => { setImportSelectedProd(e.target.value); setImportSelectedPlan(''); if(e.target.value) loadImportPlans(e.target.value); }}
                onClick={() => { if(!importProductions.length) loadImportProductions(); }}
                style={{ width:'100%',background:'#1a1d23',border:'1px solid rgba(255,255,255,0.12)',borderRadius:6,color:'#e8e6df',fontSize:13,padding:'8px 10px',outline:'none',colorScheme:'dark' }}>
                <option value="">— Seleziona stagione —</option>
                {importProductions.map(p => <option key={p.id} value={p.id}>{p.show_name || p.name}{p.season_number ? ` · Stagione ${p.season_number}` : ''}</option>)}
              </select>
            </div>

            {/* Selezione piano */}
            {importSelectedProd && (
              <div>
                <div style={{ fontSize:11,color:'rgba(232,230,223,0.45)',marginBottom:6,textTransform:'uppercase',letterSpacing:'0.08em' }}>Piano da importare</div>
                <select value={importSelectedPlan} onChange={e => setImportSelectedPlan(e.target.value)}
                  style={{ width:'100%',background:'#1a1d23',border:'1px solid rgba(255,255,255,0.12)',borderRadius:6,color:'#e8e6df',fontSize:13,padding:'8px 10px',outline:'none',colorScheme:'dark' }}>
                  <option value="">— Seleziona piano —</option>
                  {importPlans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}

            {/* Data prima TX */}
            {importSelectedPlan && (
              <div>
                <div style={{ fontSize:11,color:'rgba(232,230,223,0.45)',marginBottom:6,textTransform:'uppercase',letterSpacing:'0.08em' }}>Prima TX nuova stagione</div>
                <input type="date" value={importTxDate} onChange={e => setImportTxDate(e.target.value)}
                  style={{ width:'100%',background:'#1a1d23',border:'1px solid rgba(255,255,255,0.12)',borderRadius:6,color:'#e8e6df',fontSize:13,padding:'8px 10px',outline:'none',boxSizing:'border-box',colorScheme:'dark' }} />
              </div>
            )}

            <div style={{ display:'flex',gap:8,justifyContent:'flex-end',marginTop:4 }}>
              <button onClick={() => setShowImportModal(false)}
                style={{ background:'none',border:'1px solid rgba(255,255,255,0.1)',borderRadius:6,color:'rgba(232,230,223,0.45)',fontSize:12,padding:'7px 16px',cursor:'pointer' }}>Annulla</button>
              <button onClick={executeImport} disabled={!importSelectedPlan || !importTxDate || importLoading}
                style={{ background:'rgba(212,165,80,0.1)',border:'1px solid rgba(212,165,80,0.35)',borderRadius:6,color:'#d4a550',fontSize:12,fontWeight:700,padding:'7px 16px',cursor:importLoading?'wait':'pointer',opacity:(!importSelectedPlan||!importTxDate)?0.4:1 }}>
                {importLoading ? 'Importazione...' : 'Importa e ricalcola'}
              </button>
            </div>
          </div>
        </div>
      )}
      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeSnack} severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>

      {/* Dialog modifica produzione */}
      <Dialog open={editProdOpen} onClose={() => setEditProdOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>Modifica produzione</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
          <TextField label="Nome" fullWidth value={editProdForm.name||''} onChange={e => setEditProdForm(f => ({ ...f, name: e.target.value }))} />
          <TextField label="Cliente" fullWidth value={editProdForm.client||''} onChange={e => setEditProdForm(f => ({ ...f, client: e.target.value }))} />
          <TextField label="Codice" fullWidth value={editProdForm.code||''} onChange={e => setEditProdForm(f => ({ ...f, code: e.target.value }))} />
          <TextField label="Data riprese" type="date" fullWidth InputLabelProps={{ shrink: true }} value={editProdForm.shooting_start_date||''} onChange={e => setEditProdForm(f => ({ ...f, shooting_start_date: e.target.value }))} />
          <TextField label="Inizio post" type="date" fullWidth InputLabelProps={{ shrink: true }} value={editProdForm.post_start_date||''} onChange={e => setEditProdForm(f => ({ ...f, post_start_date: e.target.value }))} />
          <TextField label="Messa in onda" type="date" fullWidth InputLabelProps={{ shrink: true }} value={editProdForm.air_date||''} onChange={e => setEditProdForm(f => ({ ...f, air_date: e.target.value }))} />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setEditProdOpen(false)} sx={{ textTransform: 'none' }}>Annulla</Button>
          <Button variant="contained" disabled={editProdSaving || !editProdForm.name?.trim()} onClick={async () => { setEditProdSaving(true); try { const updated = await fetchJson(`/productions/${prod.id}`, { method: 'PUT', body: JSON.stringify(editProdForm) }); setProd(p => ({ ...p, ...updated })); setEditProdOpen(false); } catch(e) { alert('Errore: ' + e.message); } finally { setEditProdSaving(false); } }} sx={{ background: 'linear-gradient(135deg,#d4a550,#b8892f)', color: '#0a0a0f', fontWeight: 800, textTransform: 'none', borderRadius: '8px' }}>
            {editProdSaving ? 'Salvataggio…' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
